import React, { useMemo } from 'react';
import './TopBrokers.css';

/** Top NEPSE brokers - base list (sorting applied at runtime) */
const TOP_BROKERS_DATA = [
  { name: 'ABC Securities Pvt. Ltd.', number: 17 },
  { name: 'Vision Securities Pvt. Ltd.', number: 34 },
  { name: 'Sani Securities Company Ltd.', number: 42 },
  { name: 'Dynamic Money Managers Securities Pvt. Ltd.', number: 44 },
  { name: 'Imperial Securities Co. Pvt. Ltd.', number: 45 },
  { name: 'Online Securities Pvt. Ltd.', number: 49 },
  { name: 'Bhrikuti Stock Broking Co. Pvt. Ltd.', number: 55 },
  { name: 'Aryatara Investment & Securities Pvt. Ltd.', number: 57 },
  { name: 'Naasa Securities Company Ltd.', number: 58 },
  { name: 'Capital Max Securities Ltd.', number: 62 },
];

/**
 * TopBrokers - Displays top NEPSE brokers sorted by highest stock bought
 * @param {Object} props
 * @param {string} [props.theme] - Current theme (high-contrast, minimalist-dark, etc.)
 * @param {Array} [props.topAccumulatorsData] - API data with memberId/brokerId and accumulation
 * @param {boolean} [props.isOpen] - Whether the sidebar/panel is open
 * @param {Function} [props.onToggle] - Toggle handler from parent
 */
function TopBrokers({ theme = '', topAccumulatorsData = [], isOpen = false, onToggle }) {
  const collapsed = !isOpen;

  const sortedAndFilteredBrokers = useMemo(() => {
    const brokerIds = new Set(TOP_BROKERS_DATA.map((b) => b.number));
    const accumByBroker = new Map();
    (topAccumulatorsData || []).forEach((row) => {
      const id = parseInt(row.memberId || row.brokerId || row.MemberId || row.BrokerId, 10);
      if (!id || !brokerIds.has(id)) return;
      const acc = parseInt(row.accumulation || row.Accumulation || 0, 10);
      accumByBroker.set(id, Math.max(accumByBroker.get(id) || 0, acc));
    });

    let list = TOP_BROKERS_DATA.map((b) => ({
      ...b,
      stockBought: accumByBroker.get(b.number) ?? null,
    }));

    if (accumByBroker.size > 0) {
      list = [...list].sort((a, b) => (b.stockBought ?? 0) - (a.stockBought ?? 0));
    } else {
      list = [...list].sort((a, b) => a.number - b.number);
    }
    return list;
  }, [topAccumulatorsData]);

  const hasAccumData = useMemo(
    () => (topAccumulatorsData || []).length > 0,
    [topAccumulatorsData]
  );

  return (
    <div className={`top-brokers top-brokers--${theme || 'default'}`}>
      <button
        type="button"
        className="top-brokers__header"
        onClick={onToggle}
      >
        <h2 className="top-brokers__title">TBA</h2>
        <span className="top-brokers__toggle">
          {collapsed ? '+' : '−'}
        </span>
      </button>

      {!collapsed && (
        <>
          {hasAccumData && (
            <p className="top-brokers__subtitle">Sorted by highest stock bought</p>
          )}
          <div className="top-brokers__grid">
            {sortedAndFilteredBrokers.length === 0 ? (
              <p className="top-brokers__empty">No brokers match your search.</p>
            ) : (
              sortedAndFilteredBrokers.map((broker, index) => (
                <div
                  key={broker.number}
                  className="top-brokers__card"
                  data-broker-number={broker.number}
                >
                  <div className="top-brokers__rank">{index + 1}</div>
                  <div className="top-brokers__badge">Top Broker</div>
                  <div className="top-brokers__number">#{broker.number}</div>
                  <div className="top-brokers__name">{broker.name}</div>
                  {broker.stockBought != null && (
                    <div className="top-brokers__stock" title="Stock bought">
                      {broker.stockBought.toLocaleString()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TopBrokers;
